// Konva column-major / tiny-skia from_row order: x' = m0·x + m2·y + m4, y' = m1·x + m3·y + m5.
export type Affine = [number, number, number, number, number, number];

export function mul(m: Affine, n: Affine): Affine {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function isIdentityAffine(t: Affine): boolean {
  return (
    Math.abs(t[0] - 1) < 1e-6 &&
    Math.abs(t[1]) < 1e-6 &&
    Math.abs(t[2]) < 1e-6 &&
    Math.abs(t[3] - 1) < 1e-6 &&
    Math.abs(t[4]) < 1e-6 &&
    Math.abs(t[5]) < 1e-6
  );
}

/**
 * Average scale baked into a PSD text matrix. The parser already multiplied it into `fontSize`
 * (nominal × scale = the on-screen size), so the draw transform divides it out — and anything that
 * needs the size the designer actually TYPED divides `fontSize` by it. Returns undefined for a
 * degenerate matrix.
 */
export function textMatrixScale(matrix: readonly number[]): number | undefined {
  if (matrix.length < 4) return undefined;
  const [a, b, c, d] = matrix;
  const sX = Math.hypot(a, b);
  if (sX === 0) return undefined;
  const avg = (Math.abs(sX) + Math.abs((a * d - b * c) / sX)) / 2;
  return avg === 0 ? undefined : avg;
}

/**
 * Анизотропия матрицы: во сколько раз масштаб по X отличается от масштаба по Y (среднее = 1).
 * Наивный hypot, а НЕ QR/det из `textMatrixScale`: ровно эту величину раскладка вкладывает в масштаб
 * глифа, и матрица отрисовки обязана делить обратно ту же самую, иначе они разъезжаются. Порт
 * `box_anisotropy_scale` из движка (`adapter/glyph_layout.rs`) — одна формула на два рантайма.
 */
export function textMatrixAnisotropy(matrix: readonly number[] | undefined): readonly [number, number] {
  const [a, b, c, d] = matrix && matrix.length >= 4 ? matrix : [1, 0, 0, 1];
  const sx = Math.hypot(a, b);
  const sy = Math.hypot(c, d);
  const avg = (sx + sy) / 2;
  return avg > 0 ? [sx / avg, sy / avg] : [1, 1];
}

// Editor-parity draw transform from a PSD matrix [xx,xy,yx,yy,...]: M_node linear = the
// matrix divided by its average scale (avg already lives in fontSize), translation = box origin.
export function textMatrixDrawTransform(
  matrix: readonly number[],
  boxX: number,
  boxY: number,
  recipeScaleX: number,
  recipeScaleY: number,
): Affine | undefined {
  if (matrix.length < 4 || recipeScaleX === 0 || recipeScaleY === 0) return undefined;
  const [xx, xy, yx, yy] = matrix;
  // ag-psd text.transform is a standard column-major affine — use it as-is (no transpose).
  const a = xx;
  const b = xy;
  const c = yx;
  const d = yy;
  const avg = textMatrixScale(matrix);
  if (avg === undefined) return undefined;

  const mNode: Affine = [a / avg, b / avg, c / avg, d / avg, boxX, boxY];
  const aInv: Affine = [1 / recipeScaleX, 0, 0, 1 / recipeScaleY, -boxX / recipeScaleX, -boxY / recipeScaleY];
  const t = mul(mNode, aInv);
  return isIdentityAffine(t) ? undefined : t;
}

/** Rotation (degrees) about an arbitrary pivot: R(θ) then translate so (px,py) is fixed. */
export function rotationAboutPoint(rotationDeg: number, px: number, py: number): Affine {
  const theta = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return [cos, sin, -sin, cos, px - px * cos + py * sin, py - px * sin - py * cos];
}

/** Editor-parity draw transform for a plain `rect.rotation` (degrees), pivoting on the box centre. */
export function rectRotationDrawTransform(
  rotationDeg: number,
  boxX: number,
  boxY: number,
  width: number,
  height: number,
): Affine | undefined {
  if (!rotationDeg) return undefined;
  const t = rotationAboutPoint(rotationDeg, boxX + width / 2, boxY + height / 2);
  return isIdentityAffine(t) ? undefined : t;
}

/**
 * Interactive box rotation composed over the draw matrix, pivoting on the matrix image of the rect
 * centre (the drawn quad's visual centre) — the quad spins in place instead of orbiting the plain
 * rect centre the matrix has moved away from.
 */
export function composeBoxDrawTransform(
  matrix: Affine | undefined,
  rotationDeg: number,
  cx: number,
  cy: number,
): Affine | undefined {
  if (!matrix) {
    if (!rotationDeg) return undefined;
    const t = rotationAboutPoint(rotationDeg, cx, cy);
    return isIdentityAffine(t) ? undefined : t;
  }

  if (!rotationDeg) return matrix;
  const p = applyAffine(matrix, cx, cy);
  return mul(rotationAboutPoint(rotationDeg, p.x, p.y), matrix);
}

/** Scales an affine for a uniformly-scaled coordinate space (renderScale): linear part unchanged, translation × s. */
export function scaleAffineTranslation(t: Affine, s: number): Affine {
  return [t[0], t[1], t[2], t[3], t[4] * s, t[5] * s];
}

/** Re-pivots an affine when its target is translated by (ox,oy) (nested group offset): conjugate by T(ox,oy). */
export function offsetAffine(t: Affine, ox: number, oy: number): Affine {
  return [t[0], t[1], t[2], t[3], t[4] + ox - t[0] * ox - t[2] * oy, t[5] + oy - t[1] * ox - t[3] * oy];
}

/**
 * Re-anchors a draw affine to a new box origin: linear part kept, translation = (I−L)·origin — the closed
 * form of textMatrixDrawTransform's translation for any origin, so the transform tracks a moving/resizing box.
 */
export function reanchorAffine(t: Affine, ox: number, oy: number): Affine {
  const [a, b, c, d] = t;
  return [a, b, c, d, ox - (a * ox + c * oy), oy - (b * ox + d * oy)];
}

/** Maps a point through the affine (from_row order). */
export function applyAffine(t: Affine, x: number, y: number): { x: number; y: number } {
  return { x: t[0] * x + t[2] * y + t[4], y: t[1] * x + t[3] * y + t[5] };
}

export interface AffineDecomposition {
  translateX: number;
  translateY: number;
  rotationDeg: number;
  scaleX: number;
  scaleY: number;
  /** Horizontal shear coefficient: the slant, independent of scale (QR upper-triangular term). */
  shear: number;
}

/** QR decomposition of the linear part into rotation · scale · horizontal-shear (y-down, matches PSD TySh). */
export function decomposeAffine(t: Affine): AffineDecomposition {
  const [a, b, c, d, e, f] = t;
  const scaleX = Math.hypot(a, b);
  const rotationDeg = (Math.atan2(b, a) * 180) / Math.PI;
  const det = a * d - b * c;
  const scaleY = scaleX === 0 ? 0 : det / scaleX;
  const shear = det === 0 ? 0 : (a * c + b * d) / det;
  return { translateX: e, translateY: f, rotationDeg, scaleX, scaleY, shear };
}

/** Pure shear about the origin: horizontal `shx` (x shifts with y), vertical `shy` (y shifts with x). */
export function shearMatrix(shx: number, shy: number): Affine {
  return [1, shy, shx, 1, 0, 0];
}

/**
 * Shear keeping a pivot line fixed: horizontal `shx` pins the line y=cy (Photoshop top/bottom-handle
 * skew anchors the opposite edge), vertical `shy` pins x=cx. Equals T(cx,cy)·shear·T(-cx,-cy).
 */
export function shearAboutPivot(shx: number, shy: number, cx: number, cy: number): Affine {
  return [1, shy, shx, 1, -shx * cy, -shy * cx];
}
